import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button } from '@mui/material';
import { ModalCardFlow } from '../../_designVariant';
import {
    PageLayoutCard,
    SectionsOpenByDefaultCard,
    ProductDetailsListCard,
    DescriptionTextsPerLanguageCard,
    ProductTitleAndUrlCard,
    CategoryBreadcrumbCard,
    RelatedProductsCard,
    StockAvailabilityCard,
} from '../cards';
import StaticPageTab from '../../pages/StaticPageTab';
import ConfigCard from '../cards/ConfigCard';

/**
 * Body of the Product Detail Information modal — the largest of the
 * Product Detail config modals. Composes eight cards covering everything
 * from page-level layout to per-language description fields.
 *
 * value shape (each section optional):
 *   { layout:           { blocks: { … } }
 *     sectionsOpen:     { desktop: { … }, mobile: { … } }
 *     detailsList:      { identity: { … }, categories: { … }, descriptions: { … } }
 *     descriptionTexts: { nl: { … }, en: { … }, fr: { … } }
 *     titleAndUrl:      { showName, showBrand, … }
 *     breadcrumb:       { enabled, showRootLink, separator }
 *     related:          { maxToShow, variantsDisplay, showOutOfStock, highlightSelected }
 *     stock:            { showStockByStore, hideBelowQty, hideExactQty }
 *   }
 *
 * onChange(section, field, value) — same contract as the other modal bodies.
 * For cards whose state is grouped (sectionsOpen / detailsList /
 * descriptionTexts), `field` is the sub-group key and `value` is the new
 * sub-object the card emits.
 */
const ProductDetailInformationModal = ({ value = {}, onChange, shopFieldLabels, textTypes, detailPage, onDetailPageChange, theme, t }) => {
    // F15: the detail-text editor (WYSIWYG/Unlayer boot) mounts only after
    // the admin opens it — most visits to this modal are for the checkboxes
    // above, and the editor was the whole modal-open cost.
    const [detailTextOpen, setDetailTextOpen] = useState(false);
    const tr = (key, fb) => (typeof t === 'function' ? t(key, fb) : fb);
    const cardOnChange = (section) => (field, val) => {
        if (onChange) onChange(section, field, val);
    };

    return (
        <>
        {/* Wide modal → flow the cards into two balanced (masonry) columns on
            large screens so the canvas is actually used; single column otherwise. */}
        <ModalCardFlow>
            <PageLayoutCard
                value={value.layout}
                onChange={cardOnChange('layout')}
                theme={theme}
                t={t}
            />
            <SectionsOpenByDefaultCard
                value={value.sectionsOpen}
                onChange={cardOnChange('sectionsOpen')}
                theme={theme}
                t={t}
            />
            <ProductDetailsListCard
                value={value.detailsList}
                onChange={cardOnChange('detailsList')}
                shopFieldLabels={shopFieldLabels}
                theme={theme}
                t={t}
            />
            <DescriptionTextsPerLanguageCard
                value={value.descriptionTexts}
                onChange={cardOnChange('descriptionTexts')}
                textTypes={textTypes}
                theme={theme}
                t={t}
            />
            <ProductTitleAndUrlCard
                value={value.titleAndUrl}
                onChange={cardOnChange('titleAndUrl')}
                shopFieldLabels={shopFieldLabels}
                theme={theme}
                t={t}
            />
            <CategoryBreadcrumbCard
                value={value.breadcrumb}
                onChange={cardOnChange('breadcrumb')}
                shopFieldLabels={shopFieldLabels}
                theme={theme}
                t={t}
            />
            <RelatedProductsCard
                value={value.related}
                onChange={cardOnChange('related')}
                theme={theme}
                t={t}
            />
            <StockAvailabilityCard
                value={value.stock}
                onChange={cardOnChange('stock')}
                theme={theme}
                t={t}
            />
        </ModalCardFlow>

        {/* Full-width, OUTSIDE the 2-column masonry: the "Product information"
            free text (contents_detail / TEXT_DETAIL) shown on every product
            detail page. Reuses the static-page content editor — per-language,
            WYSIWYG or Unlayer designer with a fullscreen toggle. Only rendered
            when the parent wires the content in. */}
        {typeof onDetailPageChange === 'function' && (
            <Box sx={{ mt: 1 }}>
                <ConfigCard
                    anchor="cfg-card-pdi-detail-text"
                    title={tr('cfg_pdi_detail_text_title', 'Productinformatie (tekst op elke detailpagina)')}
                    description={tr('cfg_pdi_detail_text_desc', 'Vrije tekst onderaan elke productdetailpagina (bv. algemene info of voorwaarden). Per taal, met visuele editor of HTML-designer (fullscreen mogelijk).')}
                    theme={theme}
                >
                    {detailTextOpen ? (
                        <StaticPageTab
                            theme={theme}
                            t={t}
                            pageKey="detail"
                            hideHeader
                            value={detailPage}
                            onChange={onDetailPageChange}
                        />
                    ) : (
                        <Button variant="outlined" size="small" onClick={() => setDetailTextOpen(true)} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
                            {tr('cfg_pdi_detail_text_open', 'Tekst bewerken')}
                        </Button>
                    )}
                </ConfigCard>
            </Box>
        )}
        </>
    );
};

ProductDetailInformationModal.propTypes = {
    value:              PropTypes.object,
    onChange:           PropTypes.func,
    shopFieldLabels: PropTypes.object,
    textTypes:          PropTypes.array,
    detailPage:         PropTypes.object,
    onDetailPageChange: PropTypes.func,
    theme:              PropTypes.string,
    t:                  PropTypes.func,
};

export default ProductDetailInformationModal;
